import React, {useMemo} from 'react';
import styled from 'styled-components';
import {
    Camera,
    buildGroundPath,
    buildRoomFaces,
    getRoomCentre,
} from 'shared/components/Header/RoomPlanner/NavigationGuide/scene';

interface RoomSceneProps extends React.SVGProps<SVGSVGElement> {
    camera: Camera;
    label: string;
    className?: string;
    svgRef?: React.Ref<SVGSVGElement>;
    interactive?: boolean;
    dragging?: boolean;
    focus?: boolean;
    crosshair?: boolean;
}

export const RoomScene = ({
    camera,
    label,
    className,
    svgRef,
    interactive = false,
    dragging = false,
    focus = false,
    crosshair = false,
    ...handlers
}: RoomSceneProps) => {
    const restGround = useMemo(() => buildGroundPath(camera), []);
    const ground = focus ? buildGroundPath(camera, true) : restGround;
    const shapes = buildRoomFaces(camera, focus);
    const [cx, cy] = getRoomCentre(camera);

    return (
        <Svg
            {...handlers}
            ref={svgRef}
            className={className}
            viewBox="0 0 400 250"
            role="img"
            aria-label={label}
            $interactive={interactive}
            $dragging={dragging}>
            <path d={ground} className="ground" />
            {shapes.map((shape, index) =>
                shape.tag === 'polygon' ? (
                    <polygon
                        // eslint-disable-next-line react/no-array-index-key -- stable keys avoid remounting shapes as the camera rotates
                        key={index}
                        points={shape.points}
                        className={shape.className}
                    />
                ) : (
                    <polyline
                        // eslint-disable-next-line react/no-array-index-key -- stable keys avoid remounting shapes as the camera rotates
                        key={index}
                        points={shape.points}
                        className={shape.className}
                        strokeDasharray={shape.dashed ? '4 4' : undefined}
                    />
                )
            )}
            <circle cx={cx} cy={cy} r={3} className="centre" />
            {crosshair ? (
                <path d="M188 124H212 M200 112V136" className="focus-guide" />
            ) : null}
        </Svg>
    );
};

const Svg = styled.svg<{$interactive: boolean; $dragging: boolean}>`
    display: block;
    width: 100%;
    ${({$interactive, $dragging}) =>
        $interactive
            ? `
        cursor: ${$dragging ? 'grabbing' : 'grab'};
        touch-action: none;
        user-select: none;
    `
            : ''}

    .ground {
        fill: none;
        stroke: #dce6ed;
        stroke-width: 1;
        opacity: 0.55;
    }
    .room-floor {
        fill: #e2f1fb;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-width: 1.6;
    }
    .room-wall {
        fill: #d3eafa;
        fill-opacity: 0.6;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-width: 1.4;
    }
    .grid {
        fill: none;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-opacity: 0.18;
        stroke-width: 0.7;
    }
    .edge {
        fill: none;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-width: 1.3;
        opacity: 0.5;
    }
    .cabinet-front {
        fill: #fafcfe;
        stroke: #5485a5;
        stroke-width: 0.85;
        stroke-linejoin: round;
    }
    .cabinet-side {
        fill: #bed5e5;
        stroke: #5485a5;
        stroke-width: 0.85;
        stroke-linejoin: round;
    }
    .benchtop {
        fill: #688aa3;
        stroke: #416782;
        stroke-width: 0.8;
        stroke-linejoin: round;
    }
    .kickboard {
        fill: #7f9bac;
        stroke: none;
    }
    .cabinet-detail {
        fill: none;
        stroke: #5485a5;
        stroke-width: 0.85;
        stroke-linecap: round;
    }
    .cabinet-front.focus-cabinet {
        fill: #ffe2a0;
        stroke: #a96b08;
        stroke-width: 1.8;
    }
    .focus-guide {
        fill: none;
        stroke: #a96b08;
        stroke-width: 1;
        stroke-dasharray: 3 4;
        opacity: 0.65;
    }
    .centre {
        fill: ${({theme}) => theme.colors.secondary.main};
    }
`;
